<script setup lang="ts">
import { ref, onUnmounted, toRefs } from 'vue';
import { storeToRefs } from 'pinia';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faRotateLeft, faRotateRight } from '@fortawesome/pro-solid-svg-icons';
import useLocale from '../../composables/useLocale';
import useSettingStore from '../../stores/setting';
import useMediaStore from '../../stores/media';
import type { FontAwesomeIconSizes } from '../../types';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';

library.add([faRotateLeft, faRotateRight]);

type Emits = {
  (e: 'skip-backward', seconds: number): void;
  (e: 'skip-forward', seconds: number): void;
};
const emit = defineEmits<Emits>();

type Props = {
  showTooltips?: boolean;
  disabledBackward?: boolean;
  disabledForward?: boolean;
  skipSeconds?: number;
  buttonSize?: number;
  iconSize?: FontAwesomeIconSizes;
};
const props = withDefaults(defineProps<Props>(), {
  showTooltips: true,
  disabledBackward: false,
  disabledForward: false,
  skipSeconds: 5,
  buttonSize: 48,
  iconSize: 'xl',
});
const { disabledBackward, disabledForward, skipSeconds } = toRefs(props);

const { t } = useLocale();
const { playbackRate } = storeToRefs(useSettingStore());
const { setSkipFeedback } = useMediaStore();

const HOLD_THRESHOLD = 300;
const ACCUMULATION_RESET_MS = 1000;
const SPEED_UP_MULTIPLIER = 2.0;

const accumulatedBackward = ref(0);
const accumulatedForward = ref(0);
const isHoldingForward = ref(false);
const originalPlaybackRate = ref(1);

let resetBackwardTimer: ReturnType<typeof setTimeout> | null = null;
let resetForwardTimer: ReturnType<typeof setTimeout> | null = null;
let holdTimer: ReturnType<typeof setTimeout> | null = null;

const handleSkip = async (direction: 'backward' | 'forward') => {
  // Prevent skip if we are holding for 2x speed
  if (direction === 'forward' && isHoldingForward.value) return;

  const isBackward = direction === 'backward';
  if (isBackward ? disabledBackward.value : disabledForward.value) return;

  // Trigger haptic feedback for skip
  const platform = Capacitor.getPlatform();
  if (platform === 'ios' || platform === 'android') {
    try {
      await Haptics.impact({ style: ImpactStyle.Light });
    } catch (error) {
      console.debug('Haptics not available:', error);
    }
  }

  if (isBackward) {
    if (resetBackwardTimer) clearTimeout(resetBackwardTimer);

    accumulatedBackward.value += skipSeconds.value;
    setSkipFeedback(`-${accumulatedBackward.value}s`);
    // Always emit the fixed skip amount, not the accumulated value
    emit('skip-backward', skipSeconds.value);

    resetBackwardTimer = setTimeout(() => {
      accumulatedBackward.value = 0;
      setSkipFeedback(undefined);
    }, ACCUMULATION_RESET_MS);
  } else {
    if (resetForwardTimer) clearTimeout(resetForwardTimer);

    accumulatedForward.value += skipSeconds.value;
    setSkipFeedback(`+${accumulatedForward.value}s`);
    // Always emit the fixed skip amount, not the accumulated value
    emit('skip-forward', skipSeconds.value);

    resetForwardTimer = setTimeout(() => {
      accumulatedForward.value = 0;
      setSkipFeedback(undefined);
    }, ACCUMULATION_RESET_MS);
  }
};

const startHold = () => {
  if (disabledForward.value) return;
  holdTimer = setTimeout(async () => {
    isHoldingForward.value = true;
    originalPlaybackRate.value = playbackRate.value;
    playbackRate.value = originalPlaybackRate.value * SPEED_UP_MULTIPLIER;
    setSkipFeedback(`${SPEED_UP_MULTIPLIER}x Speed`);

    // Trigger stronger haptic feedback for speed increase
    const platform = Capacitor.getPlatform();
    if (platform === 'ios' || platform === 'android') {
      try {
        await Haptics.impact({ style: ImpactStyle.Medium });
      } catch (error) {
        console.debug('Haptics not available:', error);
      }
    }
  }, HOLD_THRESHOLD);
};

const stopHold = () => {
  if (holdTimer) clearTimeout(holdTimer);
  if (isHoldingForward.value) {
    playbackRate.value = originalPlaybackRate.value;
    setSkipFeedback(undefined); // Clear 2x feedback immediately on release
    setTimeout(() => {
      isHoldingForward.value = false;
    }, 50);
  }
};

const getLabel = (direction: 'back' | 'forward') => {
  if (direction === 'forward' && isHoldingForward.value) {
    return t('book_player.label_speed_increased', { speed: SPEED_UP_MULTIPLIER });
  }
  return t(`book_player.label_skip_${direction}_${skipSeconds.value}_seconds`);
};

onUnmounted(() => {
  if (resetBackwardTimer) clearTimeout(resetBackwardTimer);
  if (resetForwardTimer) clearTimeout(resetForwardTimer);
  if (holdTimer) clearTimeout(holdTimer);

  // Cleanup playback rate and store state
  if (isHoldingForward.value) {
    playbackRate.value = originalPlaybackRate.value;
  }
  setSkipFeedback(undefined);
});
</script>

<template>
  <div class="d-flex flex-row align-center ga-2 position-relative">
    <v-btn
      data-testid="bp-skip-backward-btn"
      :aria-label="getLabel('back')"
      color="on-brand"
      icon
      :ripple="false"
      variant="text"
      :size="buttonSize"
      :disabled="disabledBackward"
      @click="handleSkip('backward')"
    >
      <FontAwesomeIcon class="mr-0" :size="iconSize" icon="fas fa-rotate-left" />
      <v-tooltip v-if="showTooltips" activator="parent" location="top">
        {{ getLabel('back') }}
      </v-tooltip>
    </v-btn>

    <slot></slot>

    <v-btn
      data-testid="bp-skip-forward-btn"
      :aria-label="getLabel('forward')"
      color="on-brand"
      icon
      :ripple="false"
      variant="text"
      :size="buttonSize"
      :disabled="disabledForward"
      @click="handleSkip('forward')"
      @mousedown="startHold"
      @mouseup="stopHold"
      @mouseleave="stopHold"
      @blur="stopHold"
      @touchstart.passive="startHold"
      @touchend="stopHold"
      @keydown.space.prevent="startHold"
      @keyup.space="stopHold"
    >
      <FontAwesomeIcon class="ml-0" :size="iconSize" icon="fas fa-rotate-right" />
      <v-tooltip v-if="showTooltips" activator="parent" location="top">
        {{ getLabel('forward') }}
      </v-tooltip>
    </v-btn>

    <div class="d-sr-only" aria-live="polite" aria-atomic="true">
      <span v-if="accumulatedBackward > 0">
        {{ t('book_player.label_skipped_backward', { n: accumulatedBackward }) }}
      </span>
      <span v-if="accumulatedForward > 0">
        {{ t('book_player.label_skipped_forward', { n: accumulatedForward }) }}
      </span>
      <span v-if="isHoldingForward">
        {{ t('book_player.label_speed_increased', { n: SPEED_UP_MULTIPLIER }) }}
      </span>
    </div>
  </div>
</template>

<style scoped lang="scss"></style>
